import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import './Contact.css';
import { companyInfo } from '../mock';
import { toast } from 'sonner';
import useSEO from '../hooks/useSEO';

const Contact = () => {
  const [contactInfo, setContactInfo] = useState({
    phone: companyInfo.contact.phone,
    email: companyInfo.contact.email,
    address: companyInfo.contact.address
  });

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || '/public/api';
        const response = await fetch(`${API_BASE}/contact_info.php`);
        if (response.ok) {
          const text = await response.text();
          const result = JSON.parse(text);
          if (result.success && result.data) {
            setContactInfo(result.data);
          }
        }
      } catch (error) {
        console.error('Error fetching contact info:', error);
      }
    };

    fetchContactInfo();
  }, []);
  useSEO({
    title: 'İletişim - Efruz İnşaat | Bize Ulaşın ve Teklif Alın',
    description: 'Efruz İnşaat ile iletişime geçin. Projeleriniz için ücretsiz teklif alın, sorularınızı sorun. Telefon, e-posta ve adres bilgilerimiz. Uzman ekibimiz size yardımcı olmaya hazır.',
    keywords: 'efruz inşaat iletişim, inşaat teklifi, proje danışmanlığı, inşaat firması iletişim, ücretsiz teklif, inşaat danışmanlığı'
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [recaptchaToken, setRecaptchaToken] = useState('');

  useEffect(() => {
    // Set up reCAPTCHA callback
    window.handleContactRecaptchaCallback = (token) => {
      setRecaptchaToken(token);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if reCAPTCHA is completed
    if (!recaptchaToken) {
      toast.error('Lütfen reCAPTCHA doğrulamasını tamamlayın!');
      return;
    }
    
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost/efruzinsaat/public/api';
      const response = await fetch(`${API_BASE}/contacts.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          recaptcha_token: recaptchaToken
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Mesajınız başarıyla gönderildi!', {
          description: 'En kısa sürede size dönüş yapacağız.'
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        setRecaptchaToken('');
        // Reset reCAPTCHA
        if (window.grecaptcha) {
          window.grecaptcha.reset();
        }
      } else {
        toast.error('Bir hata oluştu!', {
          description: result.error || 'Mesaj gönderilemedi. Lütfen tekrar deneyin.'
        });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Bir hata oluştu!', {
        description: 'Mesaj gönderilemedi. Lütfen tekrar deneyin.'
      });
    }
  };

  return (
    <div className="contact-page">
      {/* Page Title Section */}
      <section className="page-title-section">
        <div className="container">
          <h1 className="page-title">İletişim</h1>
          <p className="page-description">
            Projeleriniz için bizimle iletişime geçin. Uzman ekibimiz size yardımcı olmaya hazır. Ücretsiz teklif almak ve sorularınızı sormak için bize ulaşın.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-content section-padding">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-wrapper">
              <h2 className="heading-2">Bize Ulaşın</h2>
              <p className="body-regular form-description">
                Projeleriniz hakkında detaylı bilgi almak için formu doldurun, size en kısa sürede dönelim.
              </p>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">Adınız Soyadınız *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="Adınızı girin"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">E-posta *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="ornek@email.com"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">Telefon</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="+90 5xx xxx xx xx"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="subject" className="form-label">Konu *</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="Proje konusu"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">Mesajınız *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="form-textarea"
                    placeholder="Projeniz hakkında detaylı bilgi verin..."
                  ></textarea>
                </div>

                <div className="form-group">
                  <div 
                    className="g-recaptcha" 
                    data-sitekey="6LeDXBQsAAAAABgD6boBK2-mXJilTug-EojMkvsJ"
                    data-callback="handleContactRecaptchaCallback"
                  ></div>
                </div>

                <button type="submit" className="btn-primary submit-btn">
                  Mesaj Gönder
                  <Send size={18} />
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="contact-info-wrapper">
              <div className="contact-info-card">
                <h3 className="heading-3">İletişim Bilgileri</h3>
                <p className="body-regular">Bize aşağıdaki kanallardan da ulaşabilirsiniz</p>

                <div className="info-items">
                  <div className="info-item">
                    <div className="info-icon">
                      <Phone size={24} />
                    </div>
                    <div className="info-content">
                      <h4 className="body-small">Telefon</h4>
                      <a href={`tel:${contactInfo.phone}`} className="body-regular">
                        {contactInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="info-item">
                    <div className="info-icon">
                      <Mail size={24} />
                    </div>
                    <div className="info-content">
                      <h4 className="body-small">E-posta</h4>
                      <a href={`mailto:${contactInfo.email}`} className="body-regular">
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="info-item">
                    <div className="info-icon">
                      <MapPin size={24} />
                    </div>
                    <div className="info-content">
                      <h4 className="body-small">Adres</h4>
                      <p className="body-regular">{contactInfo.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="map-wrapper">
                <iframe
                  title="Efruz İnşaat Konumu"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(contactInfo.address)}&output=embed`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
